import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { SeamlessGameFrame } from '@/components/lobby/SeamlessGameFrame.js';
import { EmptyState } from '@/components/lobby/EmptyState.js';
import { Button } from '@/components/ui/button.js';
import { useAuthStore } from '@/stores/auth.js';
import { mapApiError } from '@/lib/api/error.js';
import { launchGame, PopupBlockedError } from './launchGame.js';
import { fetchAllGames, GAMES_QUERY_KEYS, type LaunchMethod } from './api.js';

interface Embed {
  url: string;
  method: LaunchMethod;
  payload: Record<string, unknown> | null;
}

/**
 * Route-backed casino game shell: /casino/$code resolves the game from the
 * catalog, launches it, and mounts the provider iframe in-page — so every
 * casino game has a shareable, deep-linkable URL like the lottery/originals
 * pages. Non-iframe outcomes still work when deep-linked: popup games open
 * their tab and bounce back to /casino; internal games go to their own route.
 */
export function CasinoEmbedPage({ code }: { code: string }) {
  const { t, i18n } = useTranslation(['casino', 'common', 'games']);
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [embed, setEmbed] = useState<Embed | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const launchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!accessToken) void navigate({ to: '/login' });
  }, [accessToken, navigate]);

  // Same query (key AND fetcher) as the casino lobby so a click-through resolves
  // from cache. One page is not enough: the backend clamps it to 200 rows.
  const gamesQuery = useQuery({
    queryKey: [...GAMES_QUERY_KEYS.list({}), 'casino-all'],
    queryFn: () => fetchAllGames(),
    staleTime: 30_000,
    enabled: Boolean(accessToken),
  });

  const items = gamesQuery.data ?? [];
  const game = items.find((g) => g.code === code) ?? items.find((g) => g.id === code);

  const launchKey = game ? `${game.id}:${attempt}` : null;

  useEffect(() => {
    if (!accessToken || !game?.providerId || !launchKey) return;
    if (launchedRef.current === launchKey) return;
    launchedRef.current = launchKey;
    setFailed(false);

    void (async () => {
      try {
        const outcome = await launchGame({
          providerId: game.providerId,
          gameId: game.id,
          lang: i18n.language ?? 'th',
          hint:
            game.launchMode && game.launchMethod
              ? { mode: game.launchMode, method: game.launchMethod }
              : null,
        });
        if (outcome.kind === 'iframe') {
          setEmbed({ url: outcome.url, method: outcome.method, payload: outcome.payload });
        } else if (outcome.kind === 'internal') {
          await navigate({
            to: '/games/originals/$code',
            params: { code: outcome.code },
            search: { session: outcome.session },
          });
        } else if (outcome.kind === 'popup') {
          // Game opened in a new tab — this page has nothing to show.
          await navigate({ to: '/casino' });
        }
        // redirect: the current tab is being replaced; nothing to do.
      } catch (err) {
        if (err instanceof PopupBlockedError) {
          const url = err.url;
          toast.error(t('games:popupBlocked.title'), {
            action: { label: t('games:popupBlocked.openManually'), onClick: () => window.location.assign(url) },
            duration: 10_000,
          });
        } else {
          toast.error(mapApiError(err, t));
        }
        setFailed(true);
      }
    })();
  }, [accessToken, game, launchKey, i18n.language, navigate, t]);

  if (!accessToken) return null;

  if (embed) {
    return (
      <SeamlessGameFrame
        url={embed.url}
        method={embed.method}
        payload={embed.payload}
        title={game?.name ?? ''}
        game={game ?? null}
        onBack={() => void navigate({ to: '/casino' })}
      />
    );
  }

  const backAction = (
    <Button size="sm" onClick={() => void navigate({ to: '/casino' })}>
      {t('common:common.back')}
    </Button>
  );

  if (gamesQuery.isError || failed) {
    return (
      <EmptyState
        icon="dice"
        title={t('casino:embed.launchFailed')}
        action={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                if (gamesQuery.isError) void gamesQuery.refetch();
                setAttempt((n) => n + 1);
              }}
            >
              {t('common:common.retry')}
            </Button>
            {backAction}
          </div>
        }
      />
    );
  }

  if (!gamesQuery.isPending && !game) {
    return <EmptyState icon="dice" title={t('casino:embed.notFound')} action={backAction} />;
  }

  return (
    <div className="grid min-h-[60vh] place-items-center">
      <div className="flex flex-col items-center gap-3 text-[var(--text-secondary)]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-[var(--border-strong)] border-t-[var(--accent)]" />
        <span className="text-[13px] font-semibold">{t('common:common.loading')}</span>
      </div>
    </div>
  );
}
